import { ReactComponent as IconLeft } from "@/assets/svgs/chevron-left.svg";
import { ReactComponent as IconRight } from "@/assets/svgs/chevron-right-black.svg";
import { generateFileURLFromBase64 } from "@/utils/download";
import PropTypes from 'prop-types';
import React, { useEffect, useImperativeHandle, useMemo, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";

pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

const AttachmentViewer = React.forwardRef(({
	selectedAttachment,
}, ref) => {

	const [pageNumber, setPageNumber] = useState(1);
	const [totalPages, setTotalPages] = useState(null);
	const [preview, setPreview] = useState()

	const fileExtension = selectedAttachment?.filename ? selectedAttachment?.filename?.split(".").pop() : selectedAttachment?.name?.split(".").pop();
	const dlHref = useMemo(() => {
		return selectedAttachment?.data ? generateFileURLFromBase64(selectedAttachment?.data, 'application/pdf;base64') : selectedAttachment
	}, [selectedAttachment]);

	useEffect(() => {
		if (!selectedAttachment) {
			setPreview(undefined)
			return
		}
		if (selectedAttachment instanceof Blob) {
			const reader = new FileReader();
			reader.readAsDataURL(selectedAttachment);
			reader.onload = function (e) {
				setPreview(e.target.result);
			}
		}
	}, [selectedAttachment])

	function changePage(offset) {
		setPageNumber((prevPageNumber) => prevPageNumber + offset);
	}

	function previousPage() {
		changePage(-1);
	}

	function nextPage() {
		changePage(1);
	}

	function onDocumentLoadSuccess({ numPages }) {
		setTotalPages(numPages);
	}

	const handlePrint = () => {
		const existingIframe = document.querySelector('#iframePrint');
		if (existingIframe) existingIframe.parentNode.removeChild(existingIframe);

		let iframe = document.createElement('iframe'); //load content in an iframe to print later
		iframe.setAttribute('id', 'iframePrint')
		document.body.appendChild(iframe);

		iframe.style.display = 'none';
		if (fileExtension === "pdf") {
			iframe.src = dlHref;
		} else if (fileExtension === "jpeg" || fileExtension === "jpg" || fileExtension === "png") {
			iframe.src = preview;
		}

		if (iframe.src) {
			iframe.onload = function () {
				setTimeout(function () {
					iframe.focus();
					iframe.contentWindow.print();
				}, 1);
			};
		}
	}

	useImperativeHandle(ref, () => {
		return {
			handlePrint
		};
	}, [dlHref]);

	useEffect(() => {
		setPageNumber(1);
	}, [selectedAttachment]);

	const ConditionalRenderer = () => {
		if (fileExtension === "jpeg" || fileExtension === "jpg" || fileExtension === "png") {
			return (
				<div className="attachment_viewer_image_div" data-testid="attachment_viewer_image_div">
					<img className="attachment_viewer_image_converter_div" src={preview ?? dlHref} alt="Preview" />
				</div>
			);
		}
		if (fileExtension === "pdf") {
			return (
				<div className="attachment_viewer_pdf_div" data-testid="attachment_viewer_pdf_div">
					<Document
						file={dlHref}
						onLoadSuccess={onDocumentLoadSuccess}
					>
						<Page pageNumber={pageNumber} />
					</Document>
				</div>
			);
		}

		return (
			<div className="attachment_viewer_default_div" data-testid="attachment_viewer_default_div">
				No preview available for this file format
			</div>
		);
	};

	return (
		<>
			{fileExtension === "pdf" && (
				<div className="pdf_button_container">
					<p
						className="pdf_page_number__padding" data-testid="pdf_page_number__padding">
						<span className="pdf_current_page-number-border">{pageNumber || (totalPages ? 1 : "--")}</span> of {totalPages || "--"} pages
					</p>
					<div
						className={`file_name_button__controller ${pageNumber <= 1 ? "div__disabled" : "div__enabled"} pdf__attachment_viewer__icon`}
						data-testid={"icon_left_button"}
						onClick={previousPage}
						onKeyDown={previousPage} // Add keyboard listener
						role="none" // Add role attribute
					>
						<IconLeft />
					</div>
					<div className="pdf_attachment_viewer__padding"></div>
					<div
						className={`file_name_button__controller ${pageNumber >= totalPages ? "div__disabled" : "div__enabled"} pdf__attachment_viewer__icon`}
						data-testid={"icon_right_button"}
						onClick={nextPage}
						onKeyDown={nextPage} // Add keyboard listener
						role="none" // Add role attribute
					>
						<IconRight />
					</div>
				</div>
			)}
			<div className="attachment_viewer" data-testid="attachment_viewer">
				<div className="attachment_viewer_container">
					<ConditionalRenderer />
				</div>
			</div>
		</>
	);
});

AttachmentViewer.propTypes = {
	selectedAttachment: PropTypes.object
};

export default AttachmentViewer;
